/**
 * Modal Component — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Overlay modal dialog
 * - IMPORTANT: Does NOT close on backdrop click (HMVM rule)
 * - Only closes via explicit close button, Cancel, or Escape key
 * - Size variants (sm, md, lg, xl, full)
 * - Focus trap and keyboard navigation
 * - ARIA attributes for accessibility
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import { useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from '../../contexts/LocaleContext';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
  closeOnEscape?: boolean;
}

const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-[95vw]',
};

export function Modal({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  closeOnEscape = true,
}: ModalProps) {
  const { t } = useLocale();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      
      // Focus the modal
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      document.body.style.overflow = '';
      
      // Restore focus to previous element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--reviewer-z-modal-backdrop)] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop - DOES NOT close modal on click per HMVM rule */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
      />
      
      {/* Modal Content */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`
          relative z-[var(--reviewer-z-modal)]
          w-full ${sizeStyles[size]}
          max-h-[90vh]
          bg-[var(--reviewer-surface)]
          border border-[var(--reviewer-border-strong)]
          shadow-2xl
          flex flex-col
          focus:outline-none
        `}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--reviewer-border)]">
            <h2
              id="modal-title"
              className="text-[var(--reviewer-text)] text-[var(--reviewer-text-2xl)] font-medium font-mono m-0"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="
                p-1 -mr-1
                text-[var(--reviewer-text-secondary)]
                hover:text-[var(--reviewer-text)]
                transition-colors
                focus:outline-none
                focus-visible:ring-2 focus-visible:ring-[var(--reviewer-focus-ring)]
              "
              aria-label={t('modal.closeAria')}
            >
              <svg
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
        
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-[var(--reviewer-border)]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
